/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useState } from 'react';

import { LearnerOnboarding_ModularOnboardingInteractionType as ModularOnboardingInteraction } from '__generated__/graphql-types';

import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, InlineNotification, PageGridContainer } from '@coursera/cds-core';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import InformationBanner from 'bundles/onboarding-common/components/InformationBanner';
import useGetDefaultInferredRole from 'bundles/onboarding-common/hooks/useGetDefaultInferredRole';
import useGetOnboardingModuleConfig from 'bundles/onboarding-common/hooks/useGetOnboardingModuleConfig';
import useSetOnboardingModuleInteraction from 'bundles/onboarding-common/hooks/useSetOnboardingModuleInteraction';
import GenericOnboardingModuleRenderer from 'bundles/onboarding-common/module-renderers/GenericOnboardingModuleRenderer';
import InferredRoleModuleRenderer from 'bundles/onboarding-common/module-renderers/InferredRoleModuleRenderer';
import ModularOnboardingProvider from 'bundles/onboarding-common/providers/ModularOnboardingProvider';
import type { InferredRole, ModularOnboardingEntrypoint } from 'bundles/onboarding-common/types';
import { getShouldShowModularOnboarding } from 'bundles/onboarding-common/utils/experimentUtils';
import { mapQuestionPathToQuestionIdList } from 'bundles/onboarding-common/utils/modularOnboarding';
import inServerContext from 'bundles/ssr/util/inServerContext';

import _t from 'i18n!nls/onboarding-common';

export type SuccessMessageProps = {
  headerText: string;
  bodyText: string;
  ctaButton: {
    text: string;
    href?: string;
  };
};

export type Props = {
  entrypoint: ModularOnboardingEntrypoint;
  inferredRole?: InferredRole;
  shouldShowSuccessMessageOnCompletion?: boolean;
  successMessage?: SuccessMessageProps;
  onOnboardingCompletionSuccess?: () => void;
  className?: string;
  noPageGridContainer?: boolean;
};

const styles = {
  container: css`
    margin: var(--cds-spacing-300) auto;
    width: 100%;
  `,
};

const ModularOnboardingModuleRenderer = ({
  entrypoint,
  inferredRole,
  shouldShowSuccessMessageOnCompletion,
  successMessage,
  onOnboardingCompletionSuccess,
  className,
  noPageGridContainer,
}: Props) => {
  const skipFetchingDefaultInferredRole = !isAuthenticatedUser() || !getShouldShowModularOnboarding() || !!inferredRole;
  const { inferredRole: defaultInferredRole, inferredRoleLoading: defaultInferredRoleLoading } =
    useGetDefaultInferredRole({ skip: skipFetchingDefaultInferredRole });

  const skipFetchingModuleConfig =
    !isAuthenticatedUser() || (!inferredRole && inServerContext) || defaultInferredRoleLoading;
  const { displayModule, module } = useGetOnboardingModuleConfig(
    {
      entrypoint,
      inferredRole: inferredRole ?? defaultInferredRole,
    },
    skipFetchingModuleConfig
  );

  const [isSuccessBannerDismissed, setIsSuccessBannerDismissed] = useState(false);
  const [isModuleDismissed, setIsModuleDismissed] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const { setOnboardingModuleInteraction } = useSetOnboardingModuleInteraction();

  const handleSuccessBannerDismissal = async () => {
    setIsSuccessBannerDismissed(true);
  };

  const handleModuleDismissal = async () => {
    await setOnboardingModuleInteraction(ModularOnboardingInteraction.Dismissal);
    setIsModuleDismissed(true);
  };

  const handleOnboardingCompletionSuccess = () => {
    if (shouldShowSuccessMessageOnCompletion) {
      setShowSuccessMessage(true);
    } else if (onOnboardingCompletionSuccess) {
      onOnboardingCompletionSuccess();
    } else {
      redirectClientOrServer('/?completedOnboarding=true');
    }
  };

  const isContentDismissed = isModuleDismissed || isSuccessBannerDismissed;

  if (!isAuthenticatedUser() || !getShouldShowModularOnboarding() || !displayModule || isContentDismissed) {
    return null;
  }

  const renderSuccessMessage = () => {
    const { headerText, bodyText, ctaButton } = successMessage ?? {};

    return (
      <InformationBanner
        headerText={headerText ?? _t('Your goals have been set')}
        bodyText={bodyText ?? _t('Your personalized recommendations are ready, designed to help you reach your goals.')}
        textLayout="separate-lines"
        isDismissed={isSuccessBannerDismissed}
        onDismiss={handleSuccessBannerDismissal}
        ctaButton={{
          label: ctaButton?.text ?? _t('View my recommendations'),
          href: ctaButton?.href ?? '/?completedOnboarding=true',
        }}
      />
    );
  };

  const renderOnboardingModule = () => {
    switch (module?.__typename) {
      case 'LearnerOnboarding_GenericModule': {
        return module.questionPath ? (
          <ModularOnboardingProvider questionIds={mapQuestionPathToQuestionIdList(module.questionPath)}>
            <GenericOnboardingModuleRenderer
              onOnboardingCompletionSuccess={handleOnboardingCompletionSuccess}
              onDismiss={handleModuleDismissal}
            />
          </ModularOnboardingProvider>
        ) : null;
      }
      case 'LearnerOnboarding_InferredRoleModule': {
        const { inferredRole: inferredRoleFromModule } = module;
        return inferredRoleFromModule ? (
          <InferredRoleModuleRenderer
            inferredRole={inferredRoleFromModule}
            onConfirmRoleSuccess={handleOnboardingCompletionSuccess}
            onDismiss={handleModuleDismissal}
          />
        ) : null;
      }

      default:
        return null;
    }
  };

  const renderContent = () => {
    if (showSuccessMessage) {
      return renderSuccessMessage();
    }
    return renderOnboardingModule();
  };

  const renderContentWithPageGridContainer = () => {
    return (
      <PageGridContainer>
        <Grid item xs={12}>
          {renderContent()}
        </Grid>
      </PageGridContainer>
    );
  };

  const renderContentWithoutPageGridContainer = () => {
    return (
      <Grid item xs={12}>
        {renderContent()}
      </Grid>
    );
  };

  return (
    <ErrorBoundaryWithTagging
      componentName="ModularOnboardingModuleRenderer"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error with the modular onboarding component. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <div className={className} css={styles.container}>
        {noPageGridContainer ? renderContentWithoutPageGridContainer() : renderContentWithPageGridContainer()}
      </div>
    </ErrorBoundaryWithTagging>
  );
};

export default ModularOnboardingModuleRenderer;
